import { Clock, Effect, Option, Result, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import { evo } from "foldkit/struct";

import { settle, start } from "../../api/asyncData";
import { listApiKeys } from "../../api/commands/integrations";
import { createTask, listTasks, updateTask } from "../../api/commands/tasks";
import { type ApiError, toMessage } from "../../api/errors";
import { i18next } from "../../i18n";
import type { Page, PageReturn, Shared } from "../shared";
import { Message } from "./message";
import { Filters, type Loaded, type Model } from "./model";

/*
 * Tasks are live data (KTD8): not time-versioned, so fetches carry no asOf and
 * always show the present. While `shared.asOf` is set the view hides every
 * write (quick create, inline status/priority) and shows a "live data" notice.
 * Results are dropped unless their generation is current and, for the list,
 * their `seq` matches the latest fetch (a filter change or a newer page wins).
 */

// COMMANDS

export const FetchTasks = Command.define("FetchTasks", {
	args: {
		generation: S.Number,
		seq: S.Number,
		filters: Filters,
		cursor: S.NullOr(S.String),
	},
	messages: [Message.CompletedFetchTasks],
	execute: ({ generation, seq, filters, cursor }) =>
		Effect.all([
			Clock.currentTimeMillis,
			listTasks.run({
				query: filters.query,
				statuses: filters.statuses,
				priorities: filters.priorities,
				tags: filters.tags,
				graded: filters.graded,
				sort: filters.sort,
				cursor,
			}),
		]).pipe(
			Effect.map(([now, result]) =>
				Message.CompletedFetchTasks({ generation, seq, cursor, now, result }),
			),
		),
});
export const FetchKeys = listApiKeys.command(Message.CompletedFetchKeys);
export const CreateTask = createTask.command(Message.CompletedCreate);
export const UpdateTask = updateTask.command(Message.CompletedUpdate);

export const initialFilters: Filters = {
	query: "",
	statuses: [],
	priorities: [],
	tags: [],
	graded: "all",
	sort: "attention",
};

export const initial: Model = {
	page: "Tasks",
	filters: initialFilters,
	draftQuery: "",
	tasks: AsyncData.Idle(),
	fetchSeq: 0,
	loadingMore: false,
	keys: AsyncData.Idle(),
	collapsed: [],
	newTitle: "",
	creating: false,
	alert: Option.none(),
};

type Return = PageReturn<Model, Message>;

/** A fresh first page for the current filters. */
const refetch = (model: Model, shared: Shared): Return => {
	const seq = model.fetchSeq + 1;
	return {
		model: evo(model, {
			tasks: start,
			fetchSeq: () => seq,
			loadingMore: () => false,
		}),
		commands: [
			FetchTasks({
				generation: shared.generation,
				seq,
				filters: model.filters,
				cursor: null,
			}),
		],
	};
};

const withFilters = (
	model: Model,
	shared: Shared,
	f: (filters: Filters) => Filters,
): Return => refetch(evo(model, { filters: f }), shared);

const toggle = <A>(list: ReadonlyArray<A>, item: A): ReadonlyArray<A> =>
	list.includes(item) ? list.filter((x) => x !== item) : [...list, item];

const loaded = (model: Model): Loaded | null =>
	AsyncData.getOrElse(model.tasks, () => null);

/** The server answers a stale `ifLastUpdated` with 412 (StaleRecord); 409 is accepted too. */
const isConflict = (error: ApiError): boolean =>
	error._tag === "Http" && (error.status === 412 || error.status === 409);

const withAlert = (model: Model, text: string): Model =>
	evo(model, { alert: () => Option.some(text) });

const clearAlert = (model: Model): Model =>
	evo(model, { alert: () => Option.none() });

/** Inline edits: the row's `updated_at` guards the write. */
const updateRow = (
	model: Model,
	shared: Shared,
	id: string,
	patch: Pick<Parameters<typeof updateTask.run>[0], "status" | "priority">,
): Return => {
	const task = loaded(model)?.tasks.find((t) => t.id === id);
	if (task === undefined || Option.isSome(shared.asOf)) return { model };
	return {
		model: clearAlert(model),
		commands: [
			UpdateTask({
				id,
				ifLastUpdated: task.updated_at,
				...patch,
				generation: shared.generation,
			}),
		],
	};
};

export const page: Page<Model, Message> = {
	init: (_route, shared) => {
		const { model, commands = [] } = refetch(initial, shared);
		return {
			model: evo(model, { keys: start }),
			commands: [...commands, FetchKeys({ generation: shared.generation })],
		};
	},
	// The list ignores asOf (KTD8); only the view changes under time travel.
	changedRoute: (model) => ({ model }),
	update: (model, message, shared) =>
		Message.match<Return>(message, {
			CompletedFetchTasks: ({ generation, seq, cursor, now, result }) => {
				if (generation !== shared.generation || seq !== model.fetchSeq)
					return { model };
				const previous = loaded(model);
				const merged = Result.map(result, (page) => ({
					now,
					cursor: page.cursor,
					totals: page.totals,
					tasks:
						cursor === null || previous === null
							? page.tasks
							: [...previous.tasks, ...page.tasks],
				}));
				return {
					model: evo(model, {
						tasks: (tasks) => settle(tasks, merged),
						loadingMore: () => false,
					}),
				};
			},
			CompletedFetchKeys: ({ generation, result }) =>
				generation !== shared.generation
					? { model }
					: { model: evo(model, { keys: (keys) => settle(keys, result) }) },
			ToggledStatus: ({ status }) =>
				withFilters(model, shared, (f) =>
					evo(f, { statuses: (s) => toggle(s, status) }),
				),
			ToggledPriority: ({ priority }) =>
				withFilters(model, shared, (f) =>
					evo(f, { priorities: (p) => toggle(p, priority) }),
				),
			ToggledTag: ({ tag }) =>
				withFilters(model, shared, (f) =>
					evo(f, { tags: (t) => toggle(t, tag) }),
				),
			ChangedGradeFilter: ({ graded }) =>
				withFilters(model, shared, (f) => evo(f, { graded: () => graded })),
			ClearedFilters: () =>
				refetch(
					evo(model, {
						filters: (f) => ({ ...initialFilters, sort: f.sort }),
						draftQuery: () => "",
					}),
					shared,
				),
			ChangedSort: ({ sort }) =>
				withFilters(model, shared, (f) => evo(f, { sort: () => sort })),
			ChangedQuery: ({ value }) => ({
				model: evo(model, { draftQuery: () => value }),
			}),
			SubmittedSearch: () =>
				withFilters(model, shared, (f) =>
					evo(f, { query: () => model.draftQuery.trim() }),
				),
			ClickedLoadMore: () => {
				const cursor = loaded(model)?.cursor ?? null;
				if (cursor === null || model.loadingMore) return { model };
				const seq = model.fetchSeq + 1;
				return {
					model: evo(model, {
						fetchSeq: () => seq,
						loadingMore: () => true,
					}),
					commands: [
						FetchTasks({
							generation: shared.generation,
							seq,
							filters: model.filters,
							cursor,
						}),
					],
				};
			},
			ToggledCollapse: ({ id }) => ({
				model: evo(model, { collapsed: (c) => toggle(c, id) }),
			}),
			ChangedNewTitle: ({ value }) => ({
				model: evo(model, { newTitle: () => value }),
			}),
			SubmittedCreate: () => {
				const title = model.newTitle.trim();
				if (title === "" || model.creating || Option.isSome(shared.asOf))
					return { model };
				return {
					model: evo(clearAlert(model), { creating: () => true }),
					commands: [CreateTask({ title, generation: shared.generation })],
				};
			},
			CompletedCreate: ({ generation, result }) => {
				if (generation !== shared.generation) return { model };
				const done = evo(model, { creating: () => false });
				return Result.match(result, {
					onSuccess: () => refetch(evo(done, { newTitle: () => "" }), shared),
					onFailure: (error) => ({ model: withAlert(done, toMessage(error)) }),
				});
			},
			ChangedStatus: ({ id, status }) =>
				updateRow(model, shared, id, { status }),
			ChangedPriority: ({ id, priority }) =>
				updateRow(model, shared, id, { priority }),
			CompletedUpdate: ({ generation, result }) => {
				if (generation !== shared.generation) return { model };
				return Result.match(result, {
					onSuccess: () => refetch(model, shared),
					// Someone else changed the task: show theirs and say so.
					onFailure: (error) =>
						isConflict(error)
							? refetch(
									withAlert(model, i18next.t("tasks.list.conflict")),
									shared,
								)
							: { model: withAlert(model, toMessage(error)) },
				});
			},
			DismissedAlert: () => ({ model: clearAlert(model) }),
		}),
};
